import React, { useEffect, useMemo, useRef, useState } from "react";
import { kindOf, eventEmoji } from "@/lib/events";

/**
 * WeekViewGrid — Google-style 7-day vertical week.
 *
 * Cols = Mon..Sun, rows = hours (06:00 → 22:00 by default).
 * Events render as positioned absolute boxes inside their day column,
 * top + height computed from start/end times. All-day events get a
 * thin row at the top of each day column.
 *
 * Drag-to-move: pointerdown on an event's body → drag → drop.
 *   • Vertical move snaps to 15-min increments and updates start time.
 *   • Horizontal cross-column move re-targets the day, keeping time of day.
 *   • Resize handle at the bottom edge stretches the duration only.
 *
 * Click empty area → onCreate({ dateKey, hour, minute }).
 * Click event body (no drag) → onSelect(event).
 */
export default function WeekViewGrid({
  weekStart, // Date — the first day shown
  events,
  members,
  fadeTint,
  isParent,
  onSelect,
  onCreate,
  onPatch, // async (event, { starts_at, ends_at }) => void  — drag/resize
  hourMin = 6,
  hourMax = 23,
  numDays = 7,
  testidPrefix = "week",
}) {
  const HOUR_PX = 48; // height of one hour row
  const TOTAL_PX = (hourMax - hourMin) * HOUR_PX;
  const containerRef = useRef(null);
  const nowTickRef = useRef(0);
  const [, force] = useState(0);

  // Live "now" line — re-render every minute.
  useEffect(() => {
    const id = setInterval(() => {
      nowTickRef.current += 1;
      force((x) => x + 1);
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Build the day columns starting from weekStart.
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < numDays; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({ date: d, key: k });
    }
    return out;
  }, [weekStart, numDays]);

  // Bucket events by day key. All-day events go to a separate strip.
  const buckets = useMemo(() => {
    const m = new Map();
    const allDay = new Map();
    for (const ev of events || []) {
      const start = new Date(ev.starts_at);
      if (Number.isNaN(start.getTime())) continue;
      const end = ev.ends_at ? new Date(ev.ends_at) : null;
      const isAllDay =
        start.getHours() === 0 &&
        start.getMinutes() === 0 &&
        (!end || (end - start) >= 23 * 3600 * 1000);
      // Walk every day from start to end (capped to current week)
      // for multi-day events; for all-day we mark each day; for timed
      // we only show on the start day.
      if (isAllDay && end) {
        const cursor = new Date(start);
        cursor.setHours(0, 0, 0, 0);
        const last = new Date(end);
        last.setHours(0, 0, 0, 0);
        let safety = 0;
        while (cursor <= last && safety < 14) {
          const k = ymd(cursor);
          if (!allDay.has(k)) allDay.set(k, []);
          allDay.get(k).push(ev);
          cursor.setDate(cursor.getDate() + 1);
          safety++;
        }
      } else if (isAllDay) {
        const k = ymd(start);
        if (!allDay.has(k)) allDay.set(k, []);
        allDay.get(k).push(ev);
      } else {
        const k = ymd(start);
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(ev);
      }
    }
    return { timed: m, allDay };
  }, [events]);

  // Auto-scroll to "now" or 8 AM on mount / week change.
  useEffect(() => {
    if (!containerRef.current) return;
    const now = new Date();
    const inWeek = days.some((d) => sameDay(d.date, now));
    const targetHour = inWeek ? Math.max(hourMin, now.getHours() - 1) : 7;
    const px = (targetHour - hourMin) * HOUR_PX;
    containerRef.current.scrollTop = Math.max(0, px);
  }, [weekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drag state — single in-flight drag at a time.
  const [drag, setDrag] = useState(null);
  // drag = { eventId, mode: 'move'|'resize', startY, startCol, durationMin, ghostStart, ghostEnd, ghostDayKey }
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const cell = pickCellFromPoint(e.clientX, e.clientY);
      if (!cell) return;
      const { dayKey, minutes } = cell;
      setDrag((d) => {
        if (!d) return d;
        if (d.mode === "move") {
          const startMin = snap15(minutes);
          const startDate = new Date(`${dayKey}T00:00:00`);
          startDate.setMinutes(startMin);
          const endDate = new Date(startDate.getTime() + d.durationMin * 60_000);
          return { ...d, ghostStart: startDate, ghostEnd: endDate, ghostDayKey: dayKey };
        }
        // resize → only change endMin within the original day
        const baseStart = d.origStart;
        const desiredEnd = new Date(`${dayKey}T00:00:00`);
        desiredEnd.setMinutes(snap15(minutes));
        if (desiredEnd <= baseStart) return d;
        return { ...d, ghostStart: baseStart, ghostEnd: desiredEnd, ghostDayKey: ymd(baseStart) };
      });
    };
    const onUp = async () => {
      const final = drag;
      setDrag(null);
      if (!final || !final.ghostStart || !final.ghostEnd) return;
      const ev = (events || []).find((e) => e.id === final.eventId);
      if (!ev) return;
      const newStart = final.ghostStart.toISOString();
      const newEnd = final.ghostEnd.toISOString();
      if (newStart === ev.starts_at && newEnd === (ev.ends_at || newEnd)) return;
      try {
        await onPatch?.(ev, { starts_at: newStart, ends_at: newEnd });
      } catch {
        /* parent toasts */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp, { once: true });
    window.addEventListener("pointercancel", onUp, { once: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  function pickCellFromPoint(x, y) {
    if (!containerRef.current) return null;
    const cols = containerRef.current.querySelectorAll("[data-day-col]");
    let chosen = null;
    for (const col of cols) {
      const r = col.getBoundingClientRect();
      if (x >= r.left && x <= r.right) {
        chosen = col;
        break;
      }
    }
    if (!chosen) return null;
    const dayKey = chosen.getAttribute("data-day-col");
    const r = chosen.getBoundingClientRect();
    const dy = y - r.top + chosen.scrollTop;
    const minutes = Math.max(0, Math.min(24 * 60 - 1, hourMin * 60 + (dy / HOUR_PX) * 60));
    return { dayKey, minutes };
  }

  // Click on empty column → create.
  const handleColClick = (e, dayKey) => {
    if (drag) return;
    if (!isParent || !onCreate) return;
    if (e.target.closest("[data-event-block]")) return;
    const col = e.currentTarget;
    const r = col.getBoundingClientRect();
    const dy = e.clientY - r.top + col.scrollTop;
    const minutes = Math.max(0, hourMin * 60 + (dy / HOUR_PX) * 60);
    const snapped = snap15(minutes);
    const hour = Math.floor(snapped / 60);
    const minute = snapped % 60;
    onCreate({ dateKey: dayKey, hour, minute });
  };

  return (
    <div
      className="rounded-xl border border-[color:var(--line-soft)] bg-[color:var(--surface)] overflow-hidden"
      data-testid={`${testidPrefix}-grid`}
    >
      {/* Header — weekday + day-number per column */}
      <div className="grid" style={{ gridTemplateColumns: `48px repeat(${numDays}, 1fr)` }}>
        <div className="border-r border-b border-[color:var(--line-soft)]" />
        {days.map(({ date, key }) => {
          const isToday = sameDay(date, new Date());
          return (
            <div
              key={key}
              className={`text-center py-2 border-b border-r border-[color:var(--line-soft)] last:border-r-0 ${
                isToday ? "bg-[color:var(--accent)]/10" : ""
              }`}
              data-testid={`${testidPrefix}-header-${key}`}
            >
              <div className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--ink-3)]">
                {date.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div
                className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full font-display tabular text-[15px] ${
                  isToday
                    ? "bg-[color:var(--accent)] text-[color:var(--bg)]"
                    : "text-[color:var(--ink)]"
                }`}
              >
                {date.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day strip */}
      {(() => {
        const anyAllDay = days.some((d) => (buckets.allDay.get(d.key) || []).length > 0);
        if (!anyAllDay) return null;
        return (
          <div
            className="grid border-b border-[color:var(--line-soft)] bg-[color:var(--bg-soft)]/40"
            style={{ gridTemplateColumns: `48px repeat(${numDays}, 1fr)` }}
            data-testid={`${testidPrefix}-allday-row`}
          >
            <div className="text-[9px] uppercase tracking-[0.08em] text-[color:var(--ink-3)] flex items-center justify-end pr-1.5 pt-1.5">
              all-day
            </div>
            {days.map(({ key }) => (
              <div key={key} className="border-r border-[color:var(--line-soft)] last:border-r-0 p-1 space-y-1 min-h-[32px]">
                {(buckets.allDay.get(key) || []).map((ev) => {
                  const k = kindOf(ev.kind);
                  const tint = fadeTint
                    ? `color-mix(in srgb, var(--bg) 50%, ${k.tint})`
                    : k.tint;
                  return (
                    <button
                      key={`${ev.id}-${key}`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect?.(ev);
                      }}
                      className="block w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate"
                      style={{ background: tint }}
                      data-testid={`${testidPrefix}-allday-${ev.id}`}
                      title={ev.title}
                    >
                      <span className="mr-1">{eventEmoji(ev)}</span>
                      {ev.title}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Scrollable hour-grid body */}
      <div
        ref={containerRef}
        className="relative overflow-y-auto"
        style={{ maxHeight: "60vh" }}
      >
        <div className="grid" style={{ gridTemplateColumns: `48px repeat(${numDays}, 1fr)` }}>
          {/* Hour gutter */}
          <div className="border-r border-[color:var(--line-soft)]">
            {Array.from({ length: hourMax - hourMin }).map((_, i) => {
              const h = hourMin + i;
              return (
                <div
                  key={h}
                  className="text-[10px] tabular text-[color:var(--ink-3)] text-right pr-1.5"
                  style={{ height: HOUR_PX }}
                >
                  {String(h).padStart(2, "0")}:00
                </div>
              );
            })}
          </div>
          {days.map(({ key, date }) => {
            const items = buckets.timed.get(key) || [];
            return (
              <div
                key={key}
                data-day-col={key}
                onClick={(e) => handleColClick(e, key)}
                className="relative border-r border-[color:var(--line-soft)] last:border-r-0 cursor-pointer"
                style={{ height: TOTAL_PX }}
                data-testid={`${testidPrefix}-col-${key}`}
              >
                {/* Hour grid lines */}
                {Array.from({ length: hourMax - hourMin }).map((_, i) => (
                  <div
                    key={i}
                    className="absolute left-0 right-0 border-t border-[color:var(--line-soft)]/60"
                    style={{ top: i * HOUR_PX }}
                  />
                ))}
                {/* Now line */}
                {sameDay(date, new Date()) && <NowLine hourMin={hourMin} hourPx={HOUR_PX} />}
                {/* Drag ghost */}
                {drag && drag.ghostDayKey === key && drag.ghostStart && (
                  <DragGhost
                    start={drag.ghostStart}
                    end={drag.ghostEnd}
                    hourMin={hourMin}
                    hourPx={HOUR_PX}
                  />
                )}
                {/* Event blocks */}
                {items.map((ev) => (
                  <EventBlock
                    key={ev.id}
                    event={ev}
                    members={members}
                    hourMin={hourMin}
                    hourPx={HOUR_PX}
                    fadeTint={fadeTint}
                    canDrag={isParent && !!onPatch}
                    onSelect={onSelect}
                    onStartDrag={(mode, e) => {
                      const startD = new Date(ev.starts_at);
                      const endD = ev.ends_at ? new Date(ev.ends_at) : new Date(startD.getTime() + 60 * 60_000);
                      const durMin = Math.max(15, Math.round((endD - startD) / 60_000));
                      setDrag({
                        eventId: ev.id,
                        mode,
                        origStart: startD,
                        origEnd: endD,
                        durationMin: durMin,
                        ghostStart: startD,
                        ghostEnd: endD,
                        ghostDayKey: key,
                      });
                    }}
                    isDragging={drag && drag.eventId === ev.id}
                    testidPrefix={testidPrefix}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EventBlock({
  event, members, hourMin, hourPx, fadeTint, canDrag, onSelect, onStartDrag, isDragging, testidPrefix,
}) {
  const k = kindOf(event.kind);
  const start = new Date(event.starts_at);
  const end = event.ends_at ? new Date(event.ends_at) : new Date(start.getTime() + 60 * 60_000);
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = Math.min(24 * 60, end.getHours() * 60 + end.getMinutes() || startMin + 60);
  const top = ((startMin - hourMin * 60) / 60) * hourPx;
  const height = Math.max(20, ((endMin - startMin) / 60) * hourPx);
  const tint = fadeTint
    ? `color-mix(in srgb, var(--bg) 70%, ${k.tint})`
    : k.tint;
  const attendees = (event.member_ids || [])
    .map((id) => members?.find((m) => m.id === id))
    .filter(Boolean)
    .slice(0, 3);
  const wasDragging = useRef(false);
  return (
    <div
      data-event-block
      data-testid={`${testidPrefix}-event-${event.id}`}
      className={`absolute left-1 right-1 rounded-md text-[11px] overflow-hidden border border-black/5 group ${
        isDragging ? "opacity-60 ring-2 ring-[color:var(--accent)]" : ""
      } ${canDrag ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
      style={{ top, height, background: tint }}
      onPointerDown={(e) => {
        if (!canDrag) return;
        e.stopPropagation();
        wasDragging.current = false;
        // We don't immediately start drag; we only fire onStartDrag on
        // first move > 4px so simple clicks still go to onSelect.
        const startX = e.clientX;
        const startY = e.clientY;
        const mover = (mv) => {
          if (Math.hypot(mv.clientX - startX, mv.clientY - startY) > 4) {
            wasDragging.current = true;
            onStartDrag("move", e);
            window.removeEventListener("pointermove", mover);
          }
        };
        const upper = () => {
          window.removeEventListener("pointermove", mover);
          window.removeEventListener("pointerup", upper);
        };
        window.addEventListener("pointermove", mover);
        window.addEventListener("pointerup", upper);
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!wasDragging.current) onSelect?.(event);
      }}
    >
      <div className="px-1.5 py-1 leading-tight">
        <div className="font-medium truncate">
          <span className="mr-0.5">{eventEmoji(event)}</span>
          {event.title}
          {event.attachment_image && (
            <span
              className="ml-1 text-[10px] opacity-70"
              title="Photo attached"
              data-testid={`weekview-attachment-${event.id}`}
            >
              📎
            </span>
          )}
        </div>
        {height > 36 && (
          <div className="text-[10px] tabular opacity-70">
            {fmtTime(start)}{event.ends_at ? `–${fmtTime(end)}` : ""}
          </div>
        )}
        {height > 56 && attendees.length > 0 && (
          <div className="flex -space-x-1 mt-1">
            {attendees.map((m) =>
              m.avatar_image ? (
                <img key={m.id} src={m.avatar_image} alt="" className="w-4 h-4 rounded-full border border-white object-cover" />
              ) : (
                <span key={m.id} className="w-4 h-4 rounded-full bg-white/70 text-[9px] flex items-center justify-center border border-white">
                  {m.avatar_emoji}
                </span>
              )
            )}
          </div>
        )}
      </div>
      {canDrag && (
        <div
          className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize bg-black/0 hover:bg-black/15 transition-colors"
          onPointerDown={(e) => {
            e.stopPropagation();
            wasDragging.current = true;
            onStartDrag("resize", e);
          }}
          data-testid={`${testidPrefix}-resize-${event.id}`}
        />
      )}
    </div>
  );
}

function NowLine({ hourMin, hourPx }) {
  const now = new Date();
  const minutes = now.getHours() * 60 + now.getMinutes();
  const top = ((minutes - hourMin * 60) / 60) * hourPx;
  return (
    <div
      className="absolute left-0 right-0 z-20 pointer-events-none"
      style={{ top }}
    >
      <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-[color:var(--rough-strong)]" />
      <div className="border-t-2 border-[color:var(--rough-strong)]" />
    </div>
  );
}

function DragGhost({ start, end, hourMin, hourPx }) {
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes();
  const top = ((startMin - hourMin * 60) / 60) * hourPx;
  const height = Math.max(20, ((endMin - startMin) / 60) * hourPx);
  return (
    <div
      className="absolute left-1 right-1 rounded-md border-2 border-dashed border-[color:var(--accent)] bg-[color:var(--accent)]/10 pointer-events-none z-10 flex items-start justify-center"
      style={{ top, height }}
      data-testid="week-drag-ghost"
    >
      <div className="text-[10px] font-medium tabular text-[color:var(--accent)] mt-0.5">
        {fmtTime(start)}–{fmtTime(end)}
      </div>
    </div>
  );
}

// ---------- helpers ----------
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function snap15(min) {
  return Math.round(min / 15) * 15;
}
function fmtTime(d) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Anchor a Date to the Monday of its week (00:00 local).
export function weekStartFromDate(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const day = (out.getDay() + 6) % 7; // 0=Mon
  out.setDate(out.getDate() - day);
  return out;
}
